// components/products/VariantsPanel.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../api.js'
import { cls } from '../../utils/helpers.js'

const EMPTY_VARIANT = () => ({
  variant_type: '',
  code: '',
  label: '',
  price_delta: 0,
  required: true,
  show_on_invoice: true,
  sort_order: 0,
})

export function VariantsPanel({ productId }) {
  const [variants, setVariants] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [form, setForm] = useState(EMPTY_VARIANT())
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})

  const loadVariants = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const data = await apiFetch(`/api/products/${productId}/variants`)
      // Exclure les variantes virtuelles injectées (sans id réel)
      const list = Array.isArray(data?.variants) ? data.variants.filter(v => v.id != null) : []
      setVariants(list)
    } catch (e) {
      setErr(e?.message || 'Erreur chargement variantes')
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    loadVariants()
  }, [loadVariants])

  async function handleAdd(e) {
    e.preventDefault()
    if (!form.variant_type.trim() || !form.code.trim() || !form.label.trim()) {
      setErr('Type, code et libellé sont requis')
      return
    }
    setSaving(true)
    setErr('')
    try {
      await apiFetch(`/api/products/${productId}/variants`, { method: 'POST', body: form })
      setForm(EMPTY_VARIANT())
      await loadVariants()
    } catch (e) {
      setErr(e?.message || 'Erreur création')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveEdit(v) {
    setSaving(true)
    setErr('')
    try {
      await apiFetch(`/api/products/${productId}/variants/${v.id}`, {
        method: 'PATCH',
        body: {
          label: editForm.label,
          price_delta: Number(editForm.price_delta) || 0,
          required: !!editForm.required,
          show_on_invoice: !!editForm.show_on_invoice,
          sort_order: Number(editForm.sort_order) || 0,
        },
      })
      setEditingId(null)
      await loadVariants()
    } catch (e) {
      setErr(e?.message || 'Erreur MAJ')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(v) {
    if (!window.confirm(`Désactiver la variante "${v.label}" (${v.variant_type} / ${v.code}) ?`)) return
    setSaving(true)
    try {
      await apiFetch(`/api/products/${productId}/variants/${v.id}`, { method: 'DELETE' })
      await loadVariants()
    } catch (e) {
      setErr(e?.message || 'Erreur suppression')
    } finally {
      setSaving(false)
    }
  }

  // Grouper par variant_type
  const grouped = useMemo(() => {
    const m = {}
    for (const v of variants) {
      if (!m[v.variant_type]) m[v.variant_type] = []
      m[v.variant_type].push(v)
    }
    return m
  }, [variants])

  const inBase = 'w-full border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-slate-200'

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
      <div className="text-sm font-extrabold text-slate-900">Variantes</div>

      {err && (
        <div className="p-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">{err}</div>
      )}

      {/* Liste existante */}
      {loading ? (
        <div className="text-xs text-slate-400">Chargement…</div>
      ) : variants.length === 0 ? (
        <div className="text-xs text-slate-400">Aucune variante — utilisez le formulaire ci-dessous pour en ajouter.</div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">{type}</div>
              <div className="space-y-1">
                {items.map(v => (
                  <div key={v.id} className="bg-white border border-slate-200 rounded-xl px-3 py-2">
                    {editingId === v.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="text-[10px] text-slate-500 mb-0.5">Libellé</div>
                            <input
                              value={editForm.label}
                              onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                              className={inBase}
                            />
                          </div>
                          <div>
                            <div className="text-[10px] text-slate-500 mb-0.5">Delta prix</div>
                            <input
                              type="number"
                              value={editForm.price_delta}
                              onChange={e => setEditForm(f => ({ ...f, price_delta: e.target.value }))}
                              className={inBase}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-700">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!editForm.required}
                              onChange={e => setEditForm(f => ({ ...f, required: e.target.checked }))}
                            />
                            Requis
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!editForm.show_on_invoice}
                              onChange={e => setEditForm(f => ({ ...f, show_on_invoice: e.target.checked }))}
                            />
                            Sur facture
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(v)}
                            disabled={saving}
                            className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Sauver
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="px-3 py-1 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-mono text-[11px] text-slate-400 mr-1">{v.code}</span>
                          <span className="text-sm font-semibold text-slate-900">{v.label}</span>
                          {Number(v.price_delta) !== 0 && (
                            <span className={cls('ml-2 text-xs font-bold', Number(v.price_delta) > 0 ? 'text-emerald-700' : 'text-rose-600')}>
                              {Number(v.price_delta) > 0 ? '+' : ''}{Number(v.price_delta).toLocaleString('fr-FR')}
                            </span>
                          )}
                          {!v.required && <span className="ml-2 text-[10px] text-slate-400">optionnel</span>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={() => {
                              setEditingId(v.id)
                              setEditForm({
                                label: v.label,
                                price_delta: v.price_delta,
                                required: v.required,
                                show_on_invoice: v.show_on_invoice,
                                sort_order: v.sort_order,
                              })
                            }}
                            className="px-2 py-1 rounded-lg text-[11px] font-bold text-slate-600 border border-slate-200 hover:bg-slate-100"
                          >
                            Éditer
                          </button>
                          <button
                            onClick={() => handleDelete(v)}
                            disabled={saving}
                            className="px-2 py-1 rounded-lg text-[11px] font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 disabled:opacity-50"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire ajout */}
      <form onSubmit={handleAdd} className="border-t border-slate-200 pt-3 space-y-2">
        <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Ajouter une variante</div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">Type *</div>
            <input
              value={form.variant_type}
              onChange={e => setForm(f => ({ ...f, variant_type: e.target.value }))}
              className={inBase}
              placeholder="ex: couleur"
            />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">Code *</div>
            <input
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              className={inBase}
              placeholder="ex: ROUGE"
            />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">Libellé *</div>
            <input
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              className={inBase}
              placeholder="ex: Rouge"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 items-end">
          <div>
            <div className="text-[10px] text-slate-500 mb-0.5">Delta prix (optionnel)</div>
            <input
              type="number"
              step="1"
              value={form.price_delta}
              onChange={e => setForm(f => ({ ...f, price_delta: e.target.value }))}
              className={inBase}
              placeholder="0"
            />
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-700 pb-1">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.required}
                onChange={e => setForm(f => ({ ...f, required: e.target.checked }))}
              />
              Requis
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.show_on_invoice}
                onChange={e => setForm(f => ({ ...f, show_on_invoice: e.target.checked }))}
              />
              Sur facture
            </label>
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-950 disabled:opacity-50"
        >
          {saving ? '…' : 'Ajouter'}
        </button>
      </form>
    </div>
  )
}
